<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { locale } = useI18n()

type OsBlock = {
  id: string
  name: string
  steps: readonly string[]
  note?: string
  commands?: readonly string[]
}

const content = computed(() => {
  if (locale.value === 'en') {
    return {
      lang: 'en',
      title: 'How to turn off IPv6 | PandaFan',
      description:
        'What to do when PandaFan says IPv6 is enabled on this computer: what it affects, one switch to try first, and step-by-step instructions for Windows, macOS and Linux.',
      skipLink: 'Skip to content',
      eyebrow: 'PandaFan help · about 3 minutes',
      heading: ['Turn off IPv6', 'on your computer'],
      lead: [
        'PandaFan showed "IPv6 is enabled on this computer" because, with IPv6 on, some websites may not open, may load slowly, or may think you are not in the region of the server you connected to.',
        'Turning IPv6 off does not affect everyday browsing, and you can turn it back on any time. The steps below take about two minutes.',
        'PandaFan never changes your computer’s network settings without asking you. The choice is yours: turn off IPv6 in your system (there is a one-click button in PandaFan’s settings), or turn on "VPN Core IPv6" inside PandaFan.',
      ],
      heroPrimary: 'Go to the steps',
      heroSecondary: 'Rather not change system settings? Try this first',
      navLabel: 'On this page',
      nav: [
        { href: '#why', number: '01', label: 'What happens' },
        { href: '#try-first', number: '02', label: 'Try this first' },
        { href: '#steps', number: '03', label: 'Steps' },
        { href: '#turn-back-on', number: '04', label: 'Turn it back on' },
        { href: '#notes', number: '05', label: 'Good to know' },
      ],
      why: {
        number: '01 / What happens',
        title: 'What IPv6 does to your connection',
        reasons: [
          {
            title: 'Some websites will not open, or load slowly',
            body: 'With IPv6 on, part of what your computer sends skips PandaFan and leaves through your local network. Inside China that part gets interfered with, so certain sites spin forever, fail to open, or work only some of the time.',
          },
          {
            title: 'Websites think you are still at home',
            body: 'Because part of your traffic bypasses PandaFan, a website may see your local network location instead of the region of the PandaFan server. Services like ChatGPT and Gemini then say the region is not supported, or keep asking you to verify, because your network location does not match the server you are connected to.',
          },
          {
            title: 'Many services are stricter with IPv6',
            body: 'Many online services are stricter with visitors on IPv6 and are quicker to demand verification or refuse access. IPv6 is also still unreliable on many networks, and our servers mainly serve traffic over IPv4. Turning IPv6 off costs you nothing and removes all of this.',
          },
        ],
      },
      tryFirst: {
        number: '02 / Try this first',
        title: 'Rather not change system settings? Try one switch first',
        label: 'You can do this inside PandaFan',
        heading: 'Turn on "VPN Core IPv6"',
        headingNote: 'It helps in most cases, but it may still not be perfect.',
        steps:
          'Open PandaFan → Settings → Expert Settings → find "VPN Core IPv6" → set "VPN Core IPv6" to "Enable" → disconnect, then connect again.',
        body: 'With this on, PandaFan takes care of IPv6 as well, and that fixes it for most people. If sites still will not open, follow the steps below to turn IPv6 off in your system.',
        whyDefault:
          'Why don’t we do this for you by default? We don’t want to change your computer’s own network settings, so by default PandaFan neither turns IPv6 off for you nor blocks IPv6 traffic. Blocking it outright would make some apps wait before falling back to IPv4, which shows up as slowness or apps not working. Turning on "VPN Core IPv6" doesn’t make you browse over IPv6; it lets PandaFan take over IPv6 traffic as well, so nothing slips past PandaFan.',
      },
      steps: {
        number: '03 / Steps',
        title: 'Follow the steps for your system',
        intro: 'Find your system below. If your computer uses both Wi-Fi and a network cable, do it for both.',
      },
      osBlocks: [
        {
          id: 'windows',
          name: 'Windows 10 / 11',
          steps: [
            'Press the Windows key and R together, type ncpa.cpl and press Enter.',
            'Right-click the network you are using (Wi-Fi or Ethernet) → Properties.',
            'Untick "Internet Protocol Version 6 (TCP/IPv6)" in the list.',
            'Click OK. No restart needed.',
          ],
          note: 'You can also reach the same window from Settings → Network & Internet → Advanced network settings → More network adapter options.',
        },
        {
          id: 'macos-13',
          name: 'macOS 13 and newer',
          steps: [
            'Click the Apple menu in the top-left corner → System Settings → Network.',
            'Select the network you are using (Wi-Fi or Ethernet) → click Details….',
            'Choose TCP/IP on the left.',
            'Set "Configure IPv6" to "Link-Local Only" → click OK.',
          ],
          note: 'If your Mac asks for a password, enter the one you use to log in.',
        },
        {
          id: 'macos-12',
          name: 'macOS 12 and earlier',
          steps: [
            'Click the Apple menu → System Preferences → Network.',
            'Select the network you are using → Advanced… → TCP/IP.',
            'Set "Configure IPv6" to "Link-Local Only" (older versions call it "Off") → click OK, then Apply.',
          ],
        },
        {
          id: 'linux',
          name: 'Linux (GNOME / NetworkManager)',
          steps: [
            'Open Settings → Network → click the gear next to the network you are using.',
            'Open the IPv6 tab → set Method to "Disable" → Apply.',
            'Disconnect from that network and reconnect.',
          ],
          note: 'If you prefer the terminal, these two commands do the same (replace <connection name> with your network name):',
          commands: [
            'nmcli connection modify "<connection name>" ipv6.method disabled',
            'nmcli connection up "<connection name>"',
          ],
        },
      ] as readonly OsBlock[],
      turnBackOn: {
        number: '04 / Turn it back on',
        title: 'Turning IPv6 back on',
        body: [
          'Go back to the same place and undo the change: on Windows tick "Internet Protocol Version 6 (TCP/IPv6)" again; on macOS set "Configure IPv6" back to "Automatically"; on Linux set Method back to "Automatic".',
          'Then disconnect from the network and reconnect.',
        ],
      },
      notes: {
        number: '05 / Good to know',
        title: 'Good to know',
        items: [
          'After turning IPv6 off, disconnect PandaFan and connect again.',
          'Turning off IPv6 does not affect everyday browsing. Video, games and office apps all work as before.',
          'A few campus or company networks are IPv6-only: a network that only offers IPv6, with no IPv4, stops working when IPv6 is off. If you lose internet completely, undo the change and use the switch in section 02 instead.',
        ],
      },
      footer: {
        title: 'All set? Reconnect PandaFan once.',
        primary: 'Back to the AI services guide',
        secondary: 'Download and setup guide',
        updated: 'Page updated 4 September 2026',
      },
    }
  }

  return {
    lang: 'zh-CN',
    title: '关闭 IPv6 指南｜PandaFan',
    description: 'PandaFan 提示电脑已启用 IPv6 时该怎么办：会有什么影响、先试哪个开关，以及 Windows、macOS、Linux 关闭 IPv6 的详细步骤。',
    skipLink: '跳到正文',
    eyebrow: 'PandaFan 帮助 · 约 3 分钟',
    heading: ['关闭电脑上的', 'IPv6'],
    lead: [
      'PandaFan 提示"检测到您的电脑已启用 IPv6"，是因为开着 IPv6 时，有些网站会打不开、变慢，或者被网站认为您不在所连服务器的地区。',
      '关闭 IPv6 不影响日常上网，随时可以改回来。照着下面的步骤做，两分钟就好。',
      '未经您同意，PandaFan 不会修改电脑的网络设置。您可以自己选择：关闭系统的 IPv6（PandaFan 设置页里有一键关闭按钮），或者在 PandaFan 里打开「VPN 核心 IPv6」。',
    ],
    heroPrimary: '直接看关闭步骤',
    heroSecondary: '不想改系统？先试这个',
    navLabel: '本页目录',
    nav: [
      { href: '#why', number: '01', label: '会有什么影响' },
      { href: '#try-first', number: '02', label: '先试这个' },
      { href: '#steps', number: '03', label: '关闭步骤' },
      { href: '#turn-back-on', number: '04', label: '改回来' },
      { href: '#notes', number: '05', label: '注意事项' },
    ],
    why: {
      number: '01 / 会有什么影响',
      title: '开着 IPv6 会怎么样',
      reasons: [
        {
          title: '有些网站打不开，或者很慢',
          body: '开着 IPv6 时，电脑发出去的一部分请求不经过 PandaFan，直接从您本地的网络出去。这部分请求在国内会受到干扰，表现就是某些网站一直转圈、打不开，或者时好时坏。',
        },
        {
          title: '网站以为您还在国内',
          body: '同样因为有一部分流量绕开了 PandaFan，网站看到的可能是您本地的网络位置，而不是 PandaFan 服务器所在的地区。ChatGPT、Gemini 这类服务会因此提示"地区不支持"或反复要求验证，原因就是网络位置和当前连接的服务器不一致。',
        },
        {
          title: '很多服务对 IPv6 限制更严',
          body: '不少互联网服务对 IPv6 的访问限制更严，更容易被要求验证或直接拒绝。加上目前 IPv6 在不少网络环境下还不稳定，我们的服务器也主要通过 IPv4 提供服务，所以关掉 IPv6 没有损失，只会少很多麻烦。',
        },
      ],
    },
    tryFirst: {
      number: '02 / 先试这个',
      title: '不想改系统设置？先试一个开关',
      label: '在 PandaFan 里就能改',
      heading: '把「VPN 核心 IPv6」打开',
      headingNote: '大多数情况下有效，但可能依然不够完美。',
      steps: '打开 PandaFan → 设置 → 专家设置 → 找到「VPN 核心 IPv6」→ 把「VPN 核心 IPv6」改为「启用」→ 断开后重新连接。',
      body: '打开以后，PandaFan 会把 IPv6 也一起接管，大多数情况下问题就解决了。试完还是打不开，再按下面的步骤关闭系统的 IPv6。',
      whyDefault:
        '为什么不默认帮您处理？我们不想改动您电脑本身的网络设置，所以默认既不会替您关闭 IPv6，也不会把 IPv6 的流量拦下来。强行拦断的话，部分应用会先等待再切回 IPv4，表现为变慢甚至无法使用。打开「VPN 核心 IPv6」并不是让您用 IPv6 上网，而是让 PandaFan 把 IPv6 的流量也一起接管，这样就不会有流量绕开 PandaFan。',
    },
    steps: {
      number: '03 / 关闭步骤',
      title: '按您的系统操作',
      intro: '找到您用的系统。如果电脑既连 Wi-Fi 也插网线，两个都要设一遍。',
    },
    osBlocks: [
      {
        id: 'windows',
        name: 'Windows 10 / 11',
        steps: [
          '同时按键盘上的 Windows 键和 R 键，输入 ncpa.cpl，按回车。',
          '找到正在使用的网络（Wi-Fi 或以太网），右键点它 →「属性」。',
          '在列表里取消勾选「Internet 协议版本 6 (TCP/IPv6)」前面的方框。',
          '点「确定」。不需要重启电脑。',
        ],
        note: '也可以从「设置」→「网络和 Internet」→「高级网络设置」→「更多网络适配器选项」进入同一个窗口。',
      },
      {
        id: 'macos-13',
        name: 'macOS 13 及更新',
        steps: [
          '点屏幕左上角的苹果图标 →「系统设置」→「网络」。',
          '选中正在使用的网络（Wi-Fi 或以太网）→ 点「详细信息…」。',
          '左侧点「TCP/IP」。',
          '把「配置 IPv6」改为「仅本地链路」→ 点「好」。',
        ],
        note: '如果提示输入密码，输入您开机登录用的密码即可。',
      },
      {
        id: 'macos-12',
        name: 'macOS 12 及更早',
        steps: [
          '点苹果图标 →「系统偏好设置」→「网络」。',
          '选中正在使用的网络 → 点「高级…」→「TCP/IP」。',
          '把「配置 IPv6」改为「仅本地链路」（旧版本叫「关闭」）→ 点「好」，再点「应用」。',
        ],
      },
      {
        id: 'linux',
        name: 'Linux（GNOME / NetworkManager）',
        steps: [
          '打开「设置」→「网络」→ 点正在使用的网络右边的齿轮。',
          '切到「IPv6」标签 → 方法选「禁用」→ 点「应用」。',
          '断开这个网络，再重新连上。',
        ],
        note: '习惯用终端的话，这两条命令效果一样（把 <连接名> 换成您的网络名称）：',
        commands: [
          'nmcli connection modify "<连接名>" ipv6.method disabled',
          'nmcli connection up "<连接名>"',
        ],
      },
    ] as readonly OsBlock[],
    turnBackOn: {
      number: '04 / 改回来',
      title: '想改回来怎么办',
      body: [
        '回到刚才的位置，把改过的选项恢复：Windows 重新勾选「Internet 协议版本 6 (TCP/IPv6)」；macOS 把「配置 IPv6」改回「自动」；Linux 把方法改回「自动」。',
        '改完后断开网络，再重新连接一次。',
      ],
    },
    notes: {
      number: '05 / 注意事项',
      title: '注意事项',
      items: [
        '关掉 IPv6 之后，把 PandaFan 断开再重新连接一次。',
        '关闭 IPv6 不影响日常上网，看视频、打游戏、办公软件都照常用。',
        '个别校园网或公司网络是只有 IPv6、没有 IPv4 的网络，关掉后会完全上不了网。遇到这种情况把设置改回去，改用第 02 节里的开关。',
      ],
    },
    footer: {
      title: '设置好了？重新连接一次 PandaFan',
      primary: '返回 AI 服务使用指南',
      secondary: '下载与安装指南',
      updated: '页面更新于 2026 年 9 月 4 日',
    },
  }
})

useSeoMeta({
  title: () => content.value.title,
  description: () => content.value.description,
  ogTitle: () => content.value.title,
  ogDescription: () => content.value.description,
  twitterTitle: () => content.value.title,
  twitterDescription: () => content.value.description,
  twitterCard: 'summary_large_image',
})

useHead({
  title: () => content.value.title,
  htmlAttrs: {
    lang: () => content.value.lang,
  },
})
</script>

<template lang="pug">
.ai-guide-shell
  a.skip-link(href="#guide-main") {{ content.skipLink }}

  main#guide-main.ai-guide(tabindex="-1")
    header.guide-hero
      .hero-copy
        p.eyebrow {{ content.eyebrow }}
        h1
          span(v-for="part in content.heading" :key="part") {{ part }}
        .hero-lead
          p(v-for="paragraph in content.lead" :key="paragraph") {{ paragraph }}
        .hero-actions
          a.primary-action(href="#steps") {{ content.heroPrimary }}
          a.secondary-action(href="#try-first") {{ content.heroSecondary }}

    .guide-layout
      aside.guide-sidebar
        nav(:aria-label="content.navLabel")
          a(v-for="item in content.nav" :key="item.href" :href="item.href")
            span {{ item.number }}
            | {{ item.label }}

      article.guide-content
        section#why.guide-section(aria-labelledby="why-title")
          .section-heading
            p.section-number {{ content.why.number }}
            h2#why-title.compact-title {{ content.why.title }}

          ol.priority-list
            li.priority-item(v-for="(reason, index) in content.why.reasons" :key="reason.title")
              span.priority-number {{ String(index + 1).padStart(2, '0') }}
              .priority-copy
                .priority-title-row
                  h3 {{ reason.title }}
                p {{ reason.body }}

        section#try-first.guide-section(aria-labelledby="try-first-title")
          .section-heading
            p.section-number {{ content.tryFirst.number }}
            h2#try-first-title.compact-title {{ content.tryFirst.title }}

          .claude-code-notice(role="note" :aria-label="content.tryFirst.label")
            p.notice-label {{ content.tryFirst.label }}
            h3 {{ content.tryFirst.heading }}
            p.notice-caveat {{ content.tryFirst.headingNote }}
            p.notice-steps {{ content.tryFirst.steps }}
            p {{ content.tryFirst.body }}
            p {{ content.tryFirst.whyDefault }}

        section#steps.guide-section(aria-labelledby="steps-title")
          .section-heading
            p.section-number {{ content.steps.number }}
            h2#steps-title.compact-title {{ content.steps.title }}
            p {{ content.steps.intro }}

          .os-block(v-for="block in content.osBlocks" :key="block.id" :id="block.id")
            h3.os-name {{ block.name }}
            ol.setup-list.steps-column
              li.setup-item(v-for="(step, index) in block.steps" :key="step")
                span.setup-index {{ String(index + 1).padStart(2, '0') }}
                .setup-copy
                  p.step-text {{ step }}
            p.os-note(v-if="block.note") {{ block.note }}
            pre.os-commands(v-if="block.commands")
              code(v-for="command in block.commands" :key="command") {{ command }}

        section#turn-back-on.guide-section(aria-labelledby="turn-back-on-title")
          .section-heading
            p.section-number {{ content.turnBackOn.number }}
            h2#turn-back-on-title.compact-title {{ content.turnBackOn.title }}

          .plain-copy
            p(v-for="paragraph in content.turnBackOn.body" :key="paragraph") {{ paragraph }}

        section#notes.guide-section(aria-labelledby="notes-title")
          .section-heading
            p.section-number {{ content.notes.number }}
            h2#notes-title.compact-title {{ content.notes.title }}

          ul.check-list.steps-column
            li(v-for="item in content.notes.items" :key="item")
              span.check-mark(aria-hidden="true") ✓
              span {{ item }}

        footer.guide-footer
          .footer-copy
            p.footer-title {{ content.footer.title }}
          .footer-actions
            NuxtLink.primary-action(to="/ai-guide") {{ content.footer.primary }}
            NuxtLink.secondary-action(to="/download") {{ content.footer.secondary }}

          .official-sources
            p {{ content.footer.updated }}
</template>

<style lang="postcss" scoped>

.ai-guide-shell {
  --guide-paper: oklch(96.7% 0.014 102);
  --guide-surface: oklch(99.3% 0.008 101);
  --guide-ink: oklch(24% 0.028 139);
  --guide-muted: oklch(47% 0.024 139);
  --guide-line: oklch(83% 0.025 126);
  --guide-green: oklch(45% 0.13 139);
  --guide-green-soft: oklch(91% 0.09 127);
  --guide-lime: oklch(87% 0.18 126);
  --guide-warm: oklch(70% 0.12 75);
  width: 100%;
  align-self: stretch;
  color: var(--guide-ink);
  background: transparent;
}

.dark .ai-guide-shell {
  --guide-paper: oklch(15% 0.012 139);
  --guide-surface: oklch(19% 0.015 139);
  --guide-ink: oklch(94% 0.014 126);
  --guide-muted: oklch(72% 0.026 133);
  --guide-line: oklch(32% 0.026 139);
  --guide-green: oklch(77% 0.17 133);
  --guide-green-soft: oklch(29% 0.07 137);
  --guide-lime: oklch(80% 0.18 126);
  --guide-warm: oklch(78% 0.12 75);
}

.skip-link {
  position: fixed;
  top: 0.75rem;
  left: 0.75rem;
  z-index: 80;
  padding: 0.75rem 1rem;
  color: var(--guide-paper);
  background: var(--guide-ink);
  transform: translateY(-150%);
  transition: transform 180ms cubic-bezier(0.25, 1, 0.5, 1);
}

.skip-link:focus {
  transform: translateY(0);
}

.ai-guide {
  width: min(100%, 90rem);
  margin: 0 auto;
  padding: clamp(2rem, 6vw, 6rem) 1rem clamp(4rem, 8vw, 7rem);
}

.guide-hero {
  display: grid;
  gap: 3rem;
  padding-bottom: clamp(4rem, 8vw, 7rem);
  border-bottom: 1px solid var(--guide-line);
}

.hero-copy {
  max-width: 53rem;
  animation: guide-enter 600ms cubic-bezier(0.16, 1, 0.3, 1) both;
}

.eyebrow,
.section-number {
  color: var(--guide-green);
  font-size: 0.78rem;
  font-weight: 750;
  letter-spacing: 0.12em;
}

.eyebrow {
  margin-bottom: 1.5rem;
}

h1,
h2,
h3,
p {
  margin: 0;
}

h1,
.footer-title {
  text-wrap: balance;
}

h1 {
  max-width: 12ch;
  font-family: 'Metropolis', 'SF Pro Display', 'PingFang SC', sans-serif;
  font-size: clamp(3.2rem, 9vw, 7.7rem);
  font-weight: 760;
  line-height: 0.94;
  letter-spacing: -0.065em;
}

h1 span {
  display: block;
}

.hero-lead {
  max-width: 42rem;
  margin-top: 2rem;
  color: var(--guide-muted);
  font-size: clamp(1.05rem, 1.4vw, 1.28rem);
  line-height: 1.85;
}

.hero-lead p + p {
  margin-top: 0.8rem;
}

.hero-actions,
.footer-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 2rem;
}

.primary-action,
.secondary-action {
  display: inline-flex;
  min-height: 2.75rem;
  align-items: center;
  justify-content: center;
  padding: 0.7rem 1rem;
  border: 1px solid var(--guide-ink);
  border-radius: 999px;
  color: var(--guide-ink);
  font-size: 0.9rem;
  font-weight: 700;
  line-height: 1.2;
  text-decoration: none;
  transition:
    transform 160ms cubic-bezier(0.25, 1, 0.5, 1),
    color 160ms cubic-bezier(0.25, 1, 0.5, 1),
    background-color 160ms cubic-bezier(0.25, 1, 0.5, 1);
}

.primary-action {
  color: var(--guide-paper);
  background: var(--guide-ink);
}

.primary-action:focus-visible,
.secondary-action:focus-visible,
.guide-sidebar a:focus-visible {
  outline: 3px solid var(--guide-lime);
  outline-offset: 3px;
}

.guide-layout {
  display: grid;
  gap: 3rem;
  padding-top: clamp(3.5rem, 7vw, 6.5rem);
}

.guide-sidebar {
  display: none;
}

.guide-content {
  min-width: 0;
}

.guide-section,
.os-block {
  scroll-margin-top: 6.5rem;
}

.guide-section + .guide-section {
  margin-top: clamp(5rem, 10vw, 9rem);
}

.section-heading {
  max-width: 48rem;
  margin-bottom: 2.5rem;
}

.section-number {
  margin-bottom: 0.9rem;
}

.section-heading h2 {
  font-family: 'Metropolis', 'SF Pro Display', 'PingFang SC', sans-serif;
  font-size: clamp(2rem, 5vw, 4.25rem);
  font-weight: 740;
  line-height: 1.06;
  letter-spacing: -0.045em;
}

.section-heading h2.compact-title {
  font-size: clamp(1.55rem, 7vw, 4rem);
  text-wrap: balance;
}

.section-heading > p:last-child {
  max-width: 42rem;
  margin-top: 1.25rem;
  color: var(--guide-muted);
  font-size: 1rem;
  line-height: 1.8;
}

.priority-list,
.setup-list,
.check-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.priority-list {
  border-top: 1px solid var(--guide-line);
}

.priority-item {
  display: grid;
  grid-template-columns: 2.5rem minmax(0, 1fr);
  gap: 1rem;
  padding: 1.5rem 0;
  border-bottom: 1px solid var(--guide-line);
}

.priority-number,
.setup-index {
  color: var(--guide-green);
  font-family: 'Metropolis', 'SF Pro Display', sans-serif;
  font-size: 0.82rem;
  font-variant-numeric: tabular-nums;
  font-weight: 750;
}

.priority-title-row {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem 1rem;
}

.priority-copy h3 {
  font-size: 1.12rem;
  font-weight: 750;
  line-height: 1.35;
}

.priority-copy p,
.setup-copy p {
  max-width: 46rem;
  margin-top: 0.5rem;
  color: var(--guide-muted);
  line-height: 1.75;
}

.setup-copy .step-text {
  margin-top: 0;
  color: var(--guide-ink);
  font-size: 1.02rem;
}

.setup-list {
  display: grid;
  border-top: 1px solid var(--guide-line);
}

.setup-item {
  display: grid;
  grid-template-columns: 2.5rem minmax(0, 1fr);
  gap: 1rem;
  padding: 1.1rem 0;
  border-bottom: 1px solid var(--guide-line);
}

.os-block + .os-block {
  margin-top: 3rem;
}

.os-name {
  margin-bottom: 1rem;
  font-family: 'Metropolis', 'SF Pro Display', 'PingFang SC', sans-serif;
  font-size: clamp(1.3rem, 2.4vw, 1.8rem);
  font-weight: 760;
  line-height: 1.25;
  letter-spacing: -0.02em;
}

.os-note {
  max-width: 46rem;
  margin-top: 1rem;
  color: var(--guide-muted);
  line-height: 1.75;
}

.os-commands {
  display: grid;
  gap: 0.35rem;
  max-width: 46rem;
  margin: 0.75rem 0 0;
  padding: 1rem 1.25rem;
  overflow-x: auto;
  border: 1px solid var(--guide-line);
  background: var(--guide-surface);
  color: var(--guide-ink);
  font-size: 0.88rem;
  line-height: 1.6;
}

.os-commands code {
  display: block;
  white-space: pre;
}

.claude-code-notice {
  margin-top: 2rem;
  padding: clamp(1.5rem, 4vw, 2.5rem);
  border: 2px solid var(--guide-green);
  background: var(--guide-green-soft);
}

.notice-label {
  margin-bottom: 0.8rem;
  color: var(--guide-green);
  font-size: 0.78rem;
  font-weight: 750;
  letter-spacing: 0.12em;
}

.claude-code-notice h3 {
  font-size: clamp(1.35rem, 2.5vw, 2rem);
  font-weight: 760;
  line-height: 1.25;
}

.notice-caveat {
  margin-top: 0.35rem;
  color: var(--guide-muted);
  font-size: 0.85rem;
  line-height: 1.5;
}

.claude-code-notice > p:not(.notice-label) {
  max-width: 48rem;
  margin-top: 0.8rem;
  line-height: 1.8;
}

.claude-code-notice .notice-steps {
  font-weight: 700;
}

.plain-copy {
  max-width: 48rem;
  color: var(--guide-muted);
  line-height: 1.8;
}

.plain-copy p + p {
  margin-top: 0.8rem;
}

.check-list {
  display: grid;
  gap: 0;
  border-top: 1px solid var(--guide-line);
}

.check-list li {
  display: grid;
  grid-template-columns: 2rem minmax(0, 1fr);
  gap: 0.8rem;
  align-items: start;
  padding: 1rem 0;
  border-bottom: 1px solid var(--guide-line);
  line-height: 1.65;
}

.check-mark {
  display: grid;
  width: 1.5rem;
  height: 1.5rem;
  place-items: center;
  color: oklch(18% 0.025 139);
  background: var(--guide-lime);
  font-size: 0.82rem;
  font-weight: 800;
}

.guide-footer {
  margin-top: clamp(5rem, 10vw, 9rem);
  padding-top: 2rem;
  border-top: 4px solid var(--guide-ink);
}

.footer-copy {
  max-width: 48rem;
}

.footer-title {
  font-family: 'Metropolis', 'SF Pro Display', 'PingFang SC', sans-serif;
  font-size: clamp(1.8rem, 4vw, 3.5rem);
  font-weight: 740;
  line-height: 1.08;
  letter-spacing: -0.04em;
}

.official-sources {
  display: grid;
  gap: 0.8rem;
  margin-top: 3rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--guide-line);
  color: var(--guide-muted);
  font-size: 0.78rem;
  line-height: 1.6;
}

@media (hover: hover) {
  .primary-action:hover,
  .secondary-action:hover {
    transform: translateY(-2px);
  }

  .primary-action:hover {
    color: oklch(18% 0.025 139);
    background: var(--guide-lime);
  }

  .secondary-action:hover {
    color: var(--guide-paper);
    background: var(--guide-ink);
  }

  .guide-sidebar a:hover {
    color: var(--guide-ink);
  }
}

@media (min-width: 44rem) {
  .ai-guide {
    padding-inline: clamp(2rem, 5vw, 5rem);
  }

  .setup-list.steps-column,
  .check-list.steps-column {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (min-width: 64rem) {
  .guide-hero {
    grid-template-columns: minmax(0, 1.3fr) minmax(23rem, 0.7fr);
    align-items: end;
  }

  .guide-layout {
    grid-template-columns: 11rem minmax(0, 1fr);
    gap: clamp(3rem, 7vw, 7rem);
  }

  .guide-sidebar {
    position: sticky;
    top: 7rem;
    display: block;
    align-self: start;
  }

  .guide-sidebar nav {
    display: grid;
  }

  .guide-sidebar a {
    display: grid;
    grid-template-columns: 1.75rem minmax(0, 1fr);
    gap: 0.5rem;
    padding: 0.65rem 0;
    border-top: 1px solid var(--guide-line);
    color: var(--guide-muted);
    font-size: 0.82rem;
    font-weight: 650;
    line-height: 1.4;
    text-decoration: none;
    transition: color 160ms cubic-bezier(0.25, 1, 0.5, 1);
  }

  .guide-sidebar a:first-child {
    border-top: 0;
  }

  .guide-sidebar a span {
    color: var(--guide-green);
    font-variant-numeric: tabular-nums;
  }

  .priority-item {
    grid-template-columns: 3rem minmax(0, 1fr);
    padding-block: 1.8rem;
  }
}

@keyframes guide-enter {
  from {
    opacity: 0;
    transform: translateY(1.25rem);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .hero-copy {
    animation: none;
  }

  .primary-action,
  .secondary-action,
  .skip-link {
    transition-duration: 0.01ms;
  }
}
</style>
